import classNames from 'classnames';
import ErrorBoundary from 'components/error-boundary';
import { WIDGET_DRAGGABLE_ITEM_TYPE } from 'constants/widgets';
import React from 'react';
import { useDrag } from 'react-dnd';
import type { RemoveWidgetParams } from 'redux/modules/superreports.actions';
import type { WidgetReference, WidgetWithData } from 'redux/modules/superreports.types';
import styles from './index.module.scss';
import WidgetHeader from './WidgetHeader';

interface Props {
  title?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  spacingSize?: 'none' | 'small' | 'medium' | 'large';
  options?: React.ReactNode;
  isFlush?: boolean;
  canExpandRows?: boolean;
  widget: WidgetWithData;
  reference: string | number;
  isLoading?: boolean;
  readOnly?: boolean;

  onOptionClick?: () => void;
  onRemoveWidget?: (params: RemoveWidgetParams) => void;
  onDuplicateWidget?: (reference: string | number) => void;
  onToggleExpandRows?: (isExpanded: boolean) => void;

  onBeginDrag: (reference: WidgetReference) => void;
  onEndDrag: () => void;
  onMoveWidget: (
    reference: WidgetReference,
    targetSequence: number,
    currentSequence: number
  ) => void;
}

interface InternalProps extends Props {
  dragHandleRef?: React.Ref<HTMLDivElement>;
  dragPreviewRef?: React.Ref<HTMLDivElement>;
}

const WidgetContainer: React.FC<InternalProps> = ({
  children,
  className,
  widget,
  readOnly,
  isFlush,
  spacingSize = 'none',
  isLoading,
  title,
  options,
  dragHandleRef,
  dragPreviewRef,
  canExpandRows,

  onToggleExpandRows,
  onRemoveWidget,
  onDuplicateWidget,
}) => {
  const widgetReference = widget.id || widget._ref;

  const handleRemoveWidget = () => {
    onRemoveWidget?.({
      _ref: widget._ref,
      id: widget.id,
      sequence: widget.sequence,
    });
  };

  const handleDuplicateWidget = () => {
    if (!widgetReference) return;
    onDuplicateWidget?.(widgetReference);
  };

  const containerClass = classNames(styles.widget, className, styles[`spacing-${spacingSize}`], {
    [styles.isFlush]: isFlush,
    [styles.isLoading]: isLoading,
    [styles.readOnly]: readOnly,
  });

  const widgetTitle = title || widget.title;

  return (
    <div className={containerClass} data-testid="container-widgetheader" ref={dragPreviewRef}>
      {widgetTitle && (
        <WidgetHeader
          onDuplicateWidget={handleDuplicateWidget}
          onRemoveWidget={handleRemoveWidget}
          onToggleExpandRows={canExpandRows ? onToggleExpandRows : undefined}
          widget={widget}
          isLoading={isLoading}
          title={widgetTitle}
          readOnly={readOnly}
          ref={dragHandleRef}
          options={options}
        />
      )}
      <ErrorBoundary>{children}</ErrorBoundary>
    </div>
  );
};

const DraggableWidgetContainer: React.FC<Props> = props => {
  const { widget, isLoading, onBeginDrag, onEndDrag, onMoveWidget } = props;
  const widgetReference = (widget.id || widget._ref)!;
  const [_, handleRef, previewRef] = useDrag(
    () => ({
      type: WIDGET_DRAGGABLE_ITEM_TYPE,
      canDrag: () => !isLoading,
      item: () => {
        // CSS/DOM manipulation in the dragStart handler causes dragEnd to fire immediately in
        // Chromium-based browsers, so we need to delay it
        // https://github.com/react-dnd/react-dnd/issues/1085
        requestAnimationFrame(() => {
          onBeginDrag(widgetReference);
        });

        return {};
      },
      end: (_, monitor) => {
        onEndDrag();
        if (monitor.didDrop() && widgetReference) {
          const { targetSequence } = monitor.getDropResult<{ targetSequence: number }>()!;
          onMoveWidget(widgetReference, targetSequence, widget.sequence);
        }
      },
    }),
    [isLoading, onBeginDrag, onEndDrag, onMoveWidget, widgetReference, widget.sequence]
  );

  return <WidgetContainer {...props} dragHandleRef={handleRef} dragPreviewRef={previewRef} />;
};

const WidgetContainerWrapper: React.FC<Props> = props =>
  !props.readOnly ? <DraggableWidgetContainer {...props} /> : <WidgetContainer {...props} />;

export default WidgetContainerWrapper;
